import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../../home/components/Footer';

export default function SyntheticGrassPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openScheduler = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) widget.click();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="cursor-pointer">
              <div className="text-2xl font-bold leading-tight text-[#2C2C2C]">RW Construction</div>
              <div className="text-sm leading-tight text-gray-600">Las Vegas LLC</div>
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Home</Link>
              <Link to="/services/pool-repair" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Pool Repair</Link>
              <Link to="/services/synthetic-grass" className="text-sm font-medium text-[#FF6B35] transition-colors cursor-pointer">Synthetic Grass</Link>
              <Link to="/services/pavers" className="text-sm font-medium text-gray-700 hover:text-[#FF6B35] transition-colors cursor-pointer">Pavers</Link>
              <button onClick={openScheduler} className="bg-[#FF6B35] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#E55A2B] transition-colors whitespace-nowrap cursor-pointer">
                Get Free Estimate
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cbe4d057108c3cd79501ea4e6cd68bab.jpg"
            alt="Professional Synthetic Grass Installation Las Vegas"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-[#22C55E] px-4 py-2 rounded-full mb-6">
            <i className="ri-plant-line text-white"></i>
            <span className="text-white text-sm font-semibold">Eco-Friendly Landscaping</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Synthetic Grass Installation in Las Vegas
          </h1>
          <p className="text-white/90 text-lg leading-relaxed mb-8">
            Transform your yard with beautiful, low-maintenance synthetic grass. Save water, eliminate mowing, and enjoy a lush green lawn year-round in the Las Vegas desert climate.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-[#22C55E] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#16A34A] transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Get Free Estimate
            </button>
            <a
              href="tel:7025001790"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-500-1790
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#22C55E] text-sm font-semibold tracking-wider uppercase">Why Synthetic Grass</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Benefits of Artificial Turf</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Discover why Las Vegas homeowners are switching to synthetic grass for their yards.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-drop-line', title: 'Save Water', desc: 'Reduce water bills by up to 70% and conserve precious desert resources.', color: 'bg-blue-500' },
              { icon: 'ri-time-line', title: 'Zero Maintenance', desc: 'No mowing, watering, fertilizing, or weeding ever again.', color: 'bg-[#22C55E]' },
              { icon: 'ri-sun-line', title: 'Always Green', desc: 'Enjoy a lush, vibrant lawn 365 days a year regardless of weather.', color: 'bg-amber-500' },
              { icon: 'ri-leaf-line', title: 'Eco-Friendly', desc: 'No pesticides, herbicides, or gas-powered equipment needed.', color: 'bg-emerald-600' },
            ].map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className={`w-20 h-20 ${benefit.color} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                  <i className={`${benefit.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#22C55E] text-sm font-semibold tracking-wider uppercase">Applications</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Where We Install Synthetic Grass</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">From residential lawns to commercial properties, we install premium artificial turf for any application.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { img: 'https://readdy.ai/api/search-image?query=Beautiful%20residential%20front%20yard%20with%20synthetic%20artificial%20grass%20lawn%20modern%20Las%20Vegas%20home%20curb%20appeal%20professional%20landscaping%20sunny%20day%20clean%20simple%20background&width=600&height=400&seq=grass-app-001&orientation=landscape', title: 'Front Yards', desc: 'Boost curb appeal with a perfectly manicured lawn that stays green all year.' },
              { img: 'https://readdy.ai/api/search-image?query=Luxury%20backyard%20with%20synthetic%20grass%20lawn%20outdoor%20living%20space%20patio%20furniture%20Las%20Vegas%20home%20family%20friendly%20turf%20professional%20landscaping%20clean%20simple%20background&width=600&height=400&seq=grass-app-002&orientation=landscape', title: 'Backyards', desc: 'Create the perfect outdoor living space for family gatherings and relaxation.' },
              { img: 'https://readdy.ai/api/search-image?query=Pet%20friendly%20synthetic%20grass%20dog%20run%20area%20in%20Las%20Vegas%20backyard%20artificial%20turf%20for%20pets%20durable%20lawn%20clean%20simple%20background%20professional%20photography&width=600&height=400&seq=grass-app-003&orientation=landscape', title: 'Pet Areas', desc: 'Durable, pet-friendly turf that\'s easy to clean and resistant to digging.' },
              { img: 'https://readdy.ai/api/search-image?query=Synthetic%20grass%20putting%20green%20in%20luxury%20Las%20Vegas%20backyard%20golf%20practice%20area%20artificial%20turf%20professional%20installation%20clean%20simple%20background&width=600&height=400&seq=grass-app-004&orientation=landscape', title: 'Putting Greens', desc: 'Practice your golf game at home with professional-grade putting surfaces.' },
              { img: 'https://readdy.ai/api/search-image?query=Children%20playground%20with%20synthetic%20grass%20soft%20safe%20artificial%20turf%20Las%20Vegas%20residential%20backyard%20play%20area%20clean%20simple%20background%20professional%20photography&width=600&height=400&seq=grass-app-005&orientation=landscape', title: 'Play Areas', desc: 'Safe, cushioned surfaces for children\'s play areas and playgrounds.' },
              { img: 'https://readdy.ai/api/search-image?query=Commercial%20property%20synthetic%20grass%20landscaping%20Las%20Vegas%20business%20office%20building%20artificial%20turf%20professional%20installation%20clean%20simple%20background&width=600&height=400&seq=grass-app-006&orientation=landscape', title: 'Commercial', desc: 'Low-maintenance landscaping solutions for businesses and HOA common areas.' },
            ].map((app, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group">
                <div className="h-48 overflow-hidden">
                  <img
                    src={app.img}
                    alt={app.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#2C2C2C] mb-2">{app.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{app.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Turf Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#22C55E] text-sm font-semibold tracking-wider uppercase">Premium Quality</span>
              <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-6">Premium Synthetic Turf Products</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">
                With over 20 years of experience, we use only the highest quality synthetic grass products designed to withstand the harsh Las Vegas sun and provide years of beautiful, maintenance-free enjoyment.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'UV Resistant', desc: 'Won\'t fade or degrade under intense desert sun' },
                  { title: 'Heat Reduction', desc: 'Advanced cooling technology for comfortable surfaces' },
                  { title: 'Realistic Look', desc: 'Multi-toned blades that mimic natural grass' },
                  { title: 'Superior Drainage', desc: 'Perforated backing for excellent water drainage' },
                  { title: '15-Year Warranty', desc: 'Industry-leading warranty on all installations' },
                  { title: 'Lead-Free & Safe', desc: 'Non-toxic materials safe for kids and pets' },
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#22C55E] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#2C2C2C]">{feature.title}</h4>
                      <p className="text-gray-600 text-sm">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Certifications */}
              <div className="mt-8 p-6 bg-[#F8F6F3] rounded-xl">
                <h3 className="text-[#2C2C2C] font-semibold mb-4">Our Certifications</h3>
                <div className="flex flex-wrap gap-3">
                  {[
                    'ASTM Certified',
                    'STC Member',
                    'ILCA Certified',
                    'EPA WaterSense Partner',
                    'SNWA Approved'
                  ].map((cert, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                      <i className="ri-award-fill text-[#22C55E]"></i>
                      <span className="text-[#2C2C2C] text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Close%20up%20detail%20of%20premium%20synthetic%20artificial%20grass%20turf%20showing%20realistic%20grass%20blades%20texture%20high%20quality%20artificial%20lawn%20material%20Las%20Vegas%20landscaping%20professional%20product%20photography%20clean%20simple%20background&width=600&height=700&seq=grass-detail-001&orientation=portrait"
                alt="Premium Synthetic Grass Quality"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-[#2C2C2C]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#22C55E] text-sm font-semibold tracking-wider uppercase">Our Process</span>
            <h2 className="text-4xl font-bold text-white mt-3 mb-4">Professional Installation Process</h2>
            <p className="text-white/70 max-w-2xl mx-auto">Our expert team follows a meticulous installation process to ensure a flawless, long-lasting result.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              { step: '1', title: 'Site Assessment', desc: 'Measure and evaluate your space' },
              { step: '2', title: 'Ground Prep', desc: 'Remove existing grass and level' },
              { step: '3', title: 'Base Install', desc: 'Lay crushed rock and compact' },
              { step: '4', title: 'Turf Laying', desc: 'Roll out and seam turf sections' },
              { step: '5', title: 'Finishing', desc: 'Add infill and brush fibers' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#22C55E] text-sm font-semibold tracking-wider uppercase">Our Work</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Recent Installations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Browse our portfolio of synthetic grass installations throughout the Las Vegas Valley.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { img: 'https://readdy.ai/api/search-image?query=Completed%20synthetic%20grass%20installation%20Las%20Vegas%20front%20yard%20beautiful%20green%20lawn%20modern%20home%20curb%20appeal%20professional%20landscaping%20sunny%20day%20clean%20background&width=600&height=450&seq=grass-gallery-001&orientation=landscape', title: 'Front Yard Transformation', location: 'Henderson, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Large%20backyard%20synthetic%20grass%20installation%20Las%20Vegas%20luxury%20home%20outdoor%20living%20space%20patio%20area%20professional%20turf%20installation%20clean%20background&width=600&height=450&seq=grass-gallery-002&orientation=landscape', title: 'Backyard Paradise', location: 'Summerlin, NV' },
              { img: 'https://readdy.ai/api/search-image?query=Synthetic%20grass%20putting%20green%20installation%20Las%20Vegas%20backyard%20golf%20practice%20area%20professional%20turf%20clean%20background&width=600&height=450&seq=grass-gallery-003&orientation=landscape', title: 'Custom Putting Green', location: 'Las Vegas, NV' },
            ].map((project, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="rounded-2xl overflow-hidden mb-4 shadow-lg">
                  <img
                    src={project.img}
                    alt={project.title}
                    className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-lg font-bold text-[#2C2C2C]">{project.title}</h3>
                <p className="text-gray-500 text-sm flex items-center gap-1">
                  <i className="ri-map-pin-line text-[#22C55E]"></i>
                  {project.location}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#F8F6F3]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#22C55E] text-sm font-semibold tracking-wider uppercase">FAQ</span>
            <h2 className="text-4xl font-bold text-[#2C2C2C] mt-3 mb-4">Common Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'How long does synthetic grass last?', a: 'Our premium synthetic grass is designed to last 15-20 years with minimal maintenance. We back our installations with a 15-year warranty.' },
              { q: 'Is synthetic grass safe for pets?', a: 'Absolutely! Our turf is pet-friendly, non-toxic, and easy to clean. It\'s resistant to digging and won\'t develop brown spots from pet waste.' },
              { q: 'How much water will I save?', a: 'Most homeowners save 50-70% on their water bills after switching to synthetic grass. In Las Vegas, this can mean savings of $100-300+ per month.' },
              { q: 'Does synthetic grass get hot in summer?', a: 'We use advanced turf products with cooling technology that reduces surface temperatures. We can also install shade structures or recommend lighter colored turf for maximum comfort.' },
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-[#2C2C2C] mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-red-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready for a Beautiful, Low-Maintenance Lawn?</h2>
          <p className="text-white/90 text-lg mb-8">
            Get a free estimate for synthetic grass installation and start saving water today.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openScheduler}
              className="inline-flex items-center gap-2 bg-white text-[#22C55E] px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-calendar-schedule-line text-xl"></i>
              Get Free Estimate
            </button>
            <a
              href="tel:7025001790"
              className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-2xl"></i>
              Call 702-500-1790 Today
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
